/**
 * The words a loading screen shows — pure, so the rules are testable without a DOM.
 *
 * Why this exists (John, 2026-10-14): many Control Center and Admin screens showed a blank
 * area or plain gray boxes for several seconds while the list loaded, so a client could not
 * tell it was working. Every loading state now says WHAT is loading, in plain words.
 *
 * Honesty rules:
 *   - A single request has no measurable progress, so it shows a label and a moving
 *     indicator — never a made-up percentage.
 *   - A screen that loads SEVERAL independent things may say how many are done
 *     ("1 of 3 ready"), because that count is real.
 *   - After SLOW_LOADING_AFTER_MS the label changes so the screen never looks frozen.
 */

/** When the label switches to the "still loading" wording. */
export const SLOW_LOADING_AFTER_MS = 8000

export const DEFAULT_LOADING_LABEL = 'Loading…'
export const SLOW_LOADING_LABEL = 'Still loading — this can take a few seconds the first time'

export interface LoadingSteps {
  /** How many of the independent loads have finished. */
  done: number
  /** How many independent loads the screen waits on. */
  total: number
}

export interface LoadingMessageInput {
  label?: string
  steps?: LoadingSteps
  /** True once the wait has passed SLOW_LOADING_AFTER_MS. */
  slow?: boolean
}

/** "1 of 3 ready" — only when there is more than one real step to count. */
export function loadingStepText(steps: LoadingSteps | undefined): string | null {
  if (!steps) return null
  const total = Math.floor(steps.total)
  if (!Number.isFinite(total) || total < 2) return null
  const done = Math.min(Math.max(0, Math.floor(steps.done)), total)
  return `${done} of ${total} ready`
}

/** The main line a loading screen shows. */
export function loadingMessage({ label, steps, slow }: LoadingMessageInput): string {
  const base = slow ? SLOW_LOADING_LABEL : (label?.trim() || DEFAULT_LOADING_LABEL)
  const step = loadingStepText(steps)
  return step ? `${base} · ${step}` : base
}

/** Count how many of several independent loading flags are finished. */
export function countLoadingSteps(stillLoading: boolean[]): LoadingSteps {
  return { done: stillLoading.filter((l) => !l).length, total: stillLoading.length }
}
